// ==========================================
// Chat history drawer
// ==========================================
// ChatGPT's history sidebar, adapted to Buddy's fixed footprint. At 340px a
// persistent sidebar isn't available, so this is the pattern ChatGPT itself uses at
// narrow widths: a drawer over the conversation with a tap-to-dismiss scrim.
//
// Opens on Cmd/Ctrl+J — the shortcut 3d specified and nothing implemented until now.
//
// Everything is 1:1 with ChatGPT except Share, which has no destination in an on-prem
// ERP; Export copies the thread as Markdown instead.
//
// Rename and delete-confirm happen inline in the row rather than in dialogs, because
// "no modals" is a hard guardrail (2d).
// ==========================================

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BuddyThread, ThreadGroup } from '../models/buddy.types';
import { BuddyThreadsService, relativeTime } from '../services/buddy-threads.service';

@Component({
  selector: 'app-buddy-history',
  standalone: false,
  templateUrl: './buddy-history.component.html',
  styleUrls: ['./buddy-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuddyHistoryComponent implements OnInit, OnDestroy {
  @Output() closed = new EventEmitter<void>();

  @ViewChild('searchField') searchField?: ElementRef<HTMLInputElement>;
  @ViewChild('renameField') renameField?: ElementRef<HTMLInputElement>;

  query = '';
  groups: ThreadGroup[] = [];
  archivedGroups: ThreadGroup[] = [];
  archivedCount = 0;
  activeId = '';
  showArchived = false;

  /** Only one row menu is ever open. */
  menuThreadId: string | null = null;
  renamingThreadId: string | null = null;
  renameValue = '';
  confirmingDeleteId: string | null = null;
  /** Transient confirmation after Export copies to the clipboard. */
  copiedThreadId: string | null = null;

  private readonly destroyed$ = new Subject<void>();
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly threads: BuddyThreadsService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.threads.threads.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.refresh();
    });
    this.threads.activeId.pipe(takeUntil(this.destroyed$)).subscribe((id) => {
      this.activeId = id;
      this.cdr.markForCheck();
    });
    // Focus lands in search, matching ChatGPT's behaviour when history opens.
    setTimeout(() => this.searchField?.nativeElement.focus());
  }

  private refresh(): void {
    this.groups = this.threads.groups(this.query, false);
    this.archivedGroups = this.threads.groups(this.query, true);
    this.archivedCount = this.threads.archivedCount();
    this.cdr.markForCheck();
  }

  // ------------------------------------------
  // Search
  // ------------------------------------------

  onQuery(value: string): void {
    this.query = value;
    this.refresh();
  }

  clearQuery(): void {
    this.query = '';
    if (this.searchField) {
      this.searchField.nativeElement.value = '';
    }
    this.refresh();
  }

  snippet(thread: BuddyThread): string | null {
    return this.threads.snippetFor(thread, this.query);
  }

  age(thread: BuddyThread): string {
    return relativeTime(thread.updatedAt);
  }

  // ------------------------------------------
  // Selection
  // ------------------------------------------

  select(thread: BuddyThread): void {
    if (this.renamingThreadId === thread.id) {
      return;
    }
    this.threads.setActive(thread.id);
    this.closed.emit();
  }

  newChat(): void {
    this.threads.createThread();
    this.closed.emit();
  }

  // ------------------------------------------
  // Row menu
  // ------------------------------------------

  toggleMenu(thread: BuddyThread, event: Event): void {
    event.stopPropagation();
    this.menuThreadId = this.menuThreadId === thread.id ? null : thread.id;
    this.confirmingDeleteId = null;
    this.cdr.markForCheck();
  }

  closeMenu(): void {
    this.menuThreadId = null;
    this.confirmingDeleteId = null;
    this.cdr.markForCheck();
  }

  startRename(thread: BuddyThread, event: Event): void {
    event.stopPropagation();
    this.renamingThreadId = thread.id;
    this.renameValue = thread.title;
    this.menuThreadId = null;
    this.cdr.markForCheck();
    setTimeout(() => {
      this.renameField?.nativeElement.focus();
      this.renameField?.nativeElement.select();
    });
  }

  commitRename(): void {
    if (this.renamingThreadId) {
      this.threads.rename(this.renamingThreadId, this.renameValue);
    }
    this.cancelRename();
  }

  cancelRename(): void {
    this.renamingThreadId = null;
    this.renameValue = '';
    this.cdr.markForCheck();
  }

  togglePin(thread: BuddyThread, event: Event): void {
    event.stopPropagation();
    this.threads.togglePin(thread.id);
    this.closeMenu();
  }

  archive(thread: BuddyThread, event: Event): void {
    event.stopPropagation();
    this.threads.setArchived(thread.id, true);
    this.closeMenu();
  }

  unarchive(thread: BuddyThread, event: Event): void {
    event.stopPropagation();
    this.threads.setArchived(thread.id, false);
    this.closeMenu();
  }

  /** ChatGPT's Share has nowhere to go here, so this copies Markdown instead. */
  exportThread(thread: BuddyThread, event: Event): void {
    event.stopPropagation();
    const markdown = this.threads.exportMarkdown(thread.id);
    this.copyToClipboard(markdown);

    this.copiedThreadId = thread.id;
    this.menuThreadId = null;
    this.cdr.markForCheck();

    if (this.copiedTimer !== null) {
      clearTimeout(this.copiedTimer);
    }
    this.copiedTimer = setTimeout(() => {
      this.copiedThreadId = null;
      this.copiedTimer = null;
      this.cdr.markForCheck();
    }, 1800);
  }

  askDelete(thread: BuddyThread, event: Event): void {
    event.stopPropagation();
    this.confirmingDeleteId = thread.id;
    this.menuThreadId = null;
    this.cdr.markForCheck();
  }

  confirmDelete(thread: BuddyThread, event: Event): void {
    event.stopPropagation();
    this.threads.remove(thread.id);
    this.confirmingDeleteId = null;
    this.cdr.markForCheck();
  }

  cancelDelete(event: Event): void {
    event.stopPropagation();
    this.confirmingDeleteId = null;
    this.cdr.markForCheck();
  }

  toggleArchivedSection(): void {
    this.showArchived = !this.showArchived;
    this.cdr.markForCheck();
  }

  trackGroup(_: number, group: ThreadGroup): string {
    return group.label;
  }

  trackThread(_: number, thread: BuddyThread): string {
    return thread.id;
  }

  private copyToClipboard(text: string): void {
    // navigator.clipboard needs a secure context; fall back for plain-http ERP hosts.
    if (navigator.clipboard && window.isSecureContext) {
      void navigator.clipboard.writeText(text);
      return;
    }
    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    try {
      document.execCommand('copy');
    } finally {
      document.body.removeChild(area);
    }
  }

  ngOnDestroy(): void {
    if (this.copiedTimer !== null) {
      clearTimeout(this.copiedTimer);
    }
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
